import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, TextField, Button, Typography, FormControl, InputLabel, Select, MenuItem, Box, Grid, RadioGroup, Radio, FormControlLabel, } from '@mui/material';
import useTranslation from '../../../hooks/useTranslation';

const toNumOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const RateModal = ({ open, onClose, onSaveRate, existingRate }) => {
  const { t } = useTranslation();

  const [rate, setRate] = useState({
    rate_type: 'flat_rate',
    name: '',
    price: '',
    description: '',
    min_price: 0,
    max_price: null,
  });

  const [hasCondition, setHasCondition] = useState(false);
  const [conditionType, setConditionType] = useState('order_value');

  useEffect(() => {
    if (existingRate) {
      const maxIsNoLimit = existingRate.max_price == null;
      const condOn =
        (existingRate.min_price != null && Number(existingRate.min_price) !== 0) ||
        !maxIsNoLimit;

      setRate({
        rate_type: 'flat_rate',
        name: existingRate.name || '',
        price: existingRate.price ?? '',
        description: existingRate.description || '',
        min_price: existingRate.min_price ?? 0,
        max_price: existingRate.max_price ?? null,
      });
      setHasCondition(condOn);
      setConditionType('order_value');
    } else {
      setRate({
        rate_type: 'flat_rate',
        name: '',
        price: '',
        description: '',
        min_price: 0,
        max_price: null,
      });
      setHasCondition(false);
      setConditionType('order_value');
    }
  }, [existingRate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRate((r) => ({ ...r, [name]: value }));
  };

  const handleSave = () => {
    const payload = {
      ...rate,
      rate_type: 'flat_rate',
      price: Number(rate.price || 0),
      min_price: hasCondition ? (toNumOrNull(rate.min_price) ?? 0) : 0,
      max_price: hasCondition ? toNumOrNull(rate.max_price) : null,
    };
    onSaveRate(payload);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="rate-modal">
      <div
        style={{
          padding: 20,
          maxWidth: 520,
          margin: '32px auto',
          backgroundColor: 'white',
          borderRadius: 12,
          boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
        }}
      >
        <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
          {existingRate
            ? t('labels.rate_modal.edit_rate')
            : t('labels.rate_modal.add_rate')}
        </Typography>
        <FormControl fullWidth margin="dense" disabled>
          <InputLabel>{t('labels.rate_modal.rate_type')}</InputLabel>
          <Select
            name="rate_type"
            value="flat_rate"
            label={t('labels.rate_modal.rate_type')}
          >
            <MenuItem value="flat_rate">
              {t('labels.rate_modal.flat')}
            </MenuItem>
          </Select>
        </FormControl>
        <TextField
          label={t('labels.rate_modal.rate_name')}
          name="name"
          variant="outlined"
          fullWidth
          margin="dense"
          value={rate.name}
          onChange={handleChange}
          placeholder={t('labels.rate_modal.rate_name_ph')}
        />
        <TextField
          label={t('labels.rate_modal.description')}
          name="description"
          variant="outlined"
          fullWidth
          margin="dense"
          multiline
          rows={3}
          value={rate.description}
          onChange={handleChange}
          placeholder={t('labels.rate_modal.description_ph')}
        />
        <TextField
          label={t('labels.rate_modal.price')}
          name="price"
          type="number"
          inputMode="decimal"
          variant="outlined"
          fullWidth
          margin="dense"
          value={rate.price}
          onChange={handleChange}
          placeholder="e.g., 299"
        />
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 2,
            border: '1px solid #e0e0e0',
            backgroundColor: '#fafafa',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: hasCondition ? 1 : 0.5,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t('labels.rate_modal.conditions_title')}
            </Typography>
            <Button
              size="small"
              variant={hasCondition ? 'outlined' : 'text'}
              onClick={() => setHasCondition((prev) => !prev)}
            >
              {hasCondition
                ? t('labels.rate_modal.remove_conditions')
                : t('labels.rate_modal.add_conditions')}
            </Button>
          </Box>
          {hasCondition ? (
            <>
              <FormControl component="fieldset" sx={{ mb: 1 }}>
                <RadioGroup
                  row
                  name="conditionType"
                  value={conditionType}
                  onChange={(e) => setConditionType(e.target.value)}
                >
                  <FormControlLabel
                    value="order_value"
                    control={<Radio size="small" />}
                    label={t('labels.rate_modal.condition_order_value')}
                  />
                  <FormControlLabel
                    value="item_weight"
                    control={<Radio size="small" />}
                    label={t('labels.rate_modal.condition_item_weight')}
                    disabled
                  />
                </RadioGroup>
              </FormControl>
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('labels.rate_modal.minimum_price')}
                    name="min_price"
                    type="number"
                    inputMode="decimal"
                    variant="outlined"
                    fullWidth
                    value={rate.min_price ?? ''}
                    onChange={handleChange}
                    placeholder="0"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('labels.rate_modal.maximum_price')}
                    name="max_price"
                    type="number"
                    inputMode="decimal"
                    variant="outlined"
                    fullWidth
                    value={rate.max_price ?? ''}
                    onChange={handleChange}
                    placeholder={t('labels.rate_modal.no_limit_ph')}
                  />
                </Grid>
              </Grid>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1 }}
              >
                {t('labels.rate_modal.applies_help')}
              </Typography>
            </>
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              {t('labels.rate_modal.applies_all_orders')}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Button onClick={onClose} variant="outlined">
            {t('labels.shipping_zone_modal.cancel')}
          </Button>\
          <Button onClick={handleSave} variant="contained">
            {existingRate ? t('actions.update') : t('labels.shipping_zone_modal.save')}
          </Button>
        </Box>
      </div>
    </Modal>
  );
};

RateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaveRate: PropTypes.func.isRequired,
  existingRate: PropTypes.object,
};

export default RateModal;